/**
 * memberSatellites — turn a laid-out flow into that flow PLUS one satellite per
 * consumed MEMBER (and per operator-added MCP server), in the SAME vocabulary
 * `withModelSatellites` already uses for models: an icon with a caption, joined
 * to whatever consumes it by a dashed connector.
 *
 * Pure: takes nodes + edges + the resolved member list, returns new
 * nodes/edges. Nothing is derived here — the join (declaration → binding ledger
 * → row → recursion) happens ONCE, server-side, in
 * `backend/src/services/workflow-composition.js`.
 *
 * ── WHERE A SATELLITE HANGS, and when a CONNECTOR is drawn ──────────────────
 *
 * A connector is an ASSERTION — "this consumes that" — so it is drawn only when
 * the platform actually knows the consumer:
 *
 *   • a NODE of this agent consumes it. The template declares `uses:
 *     ['<alias>']` on that node and the backend resolves it into `nodes: [...]`.
 *     One satellite per declaring node, connector and all. This is the target
 *     state and it needs `@zibby/agent-workflow` ≥ 2.0.5, whose `serialize()`
 *     whitelist finally carries `uses` through.
 *
 *   • a CHILD AGENT consumes it (`depth ≥ 1`, with `owner`). It is not this
 *     agent's to call — permissions resolve per ROW — so it hangs off that
 *     CHILD's own card, which this canvas already draws as a member box. The
 *     consumer is known there, so the line is true.
 *
 *   • NOBODY NAMES A CONSUMER (`nodes: []`, which is every own member until a
 *     template ships `uses:`). Then it belongs to the AGENT and NO CONNECTOR IS
 *     DRAWN. An earlier pass anchored these to the start node and labelled the
 *     line `uses`; the founder read exactly what that line says — "Start uses
 *     gbrain" — and it is false. They sit unattached above the flow instead,
 *     where they claim nothing about any step.
 *
 * Operator-added MCP servers are always in that last class, and for them it is
 * the literal truth rather than a fallback: a template is authored before the
 * agent exists, so nothing could ever have named them at node level, and at run
 * time every attached MCP entry is merged into EVERY model node's `mcpServers`
 * (workflow-executor → ZIBBY_CUSTOM_MCP → strategy-registry). Available to the
 * whole agent is what is true.
 *
 * NOTE, and it is why nothing here implies enforcement: that same runtime fact
 * means `uses:` is a declaration of INTENT that drives this drawing and the
 * docs, NOT an injection gate on who may call the server.
 */
import { DISPATCH_MEMBER_PREFIX, isDispatchMemberId } from '../../../utils/dispatchMarker';
import { capName, capKey } from '../../../utils/agentCapabilities';

export const MEMBER_NODE_PREFIX = '__memberof__';

// Same offsets the model satellite uses, so a node carrying both puts them in
// one lane rather than two competing ones. Each extra satellite steps right by
// one slot.
const SAT_DX = 104;
// EXPORTED because it is the top of a node's ATTACHMENT LANE, and the edge
// renderer has to know where that lane is so it never routes a line (or parks a
// label chip) across the satellites — see ATTACHMENT_LANE in ReadonlyLabeledEdge.
// One declaration, two consumers; do not re-type the number over there.
export const SAT_DY = 92;
const SAT_STEP = 132;

// The unattached AGENT-level row: above the flow's top-left, clear of it.
const AGENT_ROW_DY = 150;

/** The member box on this canvas that stands for `owner`, or '' if none does. */
function ownerBoxId(owner, nodes, memberSlugByUuid) {
  if (!owner || !owner.uuid) return '';
  const slug = memberSlugByUuid[owner.uuid] || owner.workflowType || '';
  if (!slug) return '';
  const hit = nodes.find((n) => n && isDispatchMemberId(n.id)
    && n.id.slice(n.id.lastIndexOf('__') + 2) === slug);
  return hit ? hit.id : '';
}

/**
 * @param {object[]} nodes            laid-out React Flow nodes
 * @param {object[]} edges            laid-out React Flow edges
 * @param {object}   ctx
 * @param {object[]} ctx.capabilities workflow.capabilities (the rollup)
 * @param {object[]} ctx.members      workflow.members (for owner slug lookup)
 * @param {object[]} ctx.customMcp    hand-added MCP servers (already deduped)
 * @param {object}   ctx.capReady     capKey → true|false|null
 * @param {object}   ctx.capIcons     capKey → iconUrl
 * @param {Function} ctx.onOpenMember (m) => void
 * @param {Function} ctx.onOpenSettings () => void
 * @returns {{nodes: object[], edges: object[]}}
 */
export function withMemberSatellites(nodes, edges, ctx) {
  if (!Array.isArray(nodes) || nodes.length === 0) return { nodes, edges };
  const {
    capabilities = [], members = [], customMcp = [], capReady = {}, capIcons = {},
    onOpenMember, onOpenSettings,
  } = ctx || {};
  if (capabilities.length === 0 && customMcp.length === 0) return { nodes, edges };

  const byId = new Map(nodes.map((n) => [n.id, n]));
  // The member box's id ends in the DECLARED slug, which is NOT the owner's
  // deployed workflowType: a member installed under a taken slug is renamed
  // (MAGNUM's KB deploys as `board-runner-gbrain-kb`). Resolve through this
  // row's own member list BY UUID — the same ledger every other lookup uses.
  const memberSlugByUuid = {};
  for (const m of members) if (m && m.uuid) memberSlugByUuid[m.uuid] = m.slug || m.workflowType || '';

  const satNodes = [];
  const satEdges = [];
  const perParent = new Map();   // parentId → how many satellites it already carries

  const attach = (parentId, key, data) => {
    const parent = byId.get(parentId);
    if (!parent) return false;
    const seat = perParent.get(parentId) || 0;
    perParent.set(parentId, seat + 1);
    const id = `${MEMBER_NODE_PREFIX}${parentId}__${key}`;
    satNodes.push({
      id,
      type: 'memberNode',
      draggable: false,
      selectable: false,
      position: {
        x: (parent.position?.x || 0) + SAT_DX + seat * SAT_STEP,
        y: (parent.position?.y || 0) + SAT_DY,
      },
      data: { ...data, attached: true },
    });
    satEdges.push({
      id: `e${id}`,
      source: parentId,
      sourceHandle: 'bottom',
      target: id,
      targetHandle: 'disc',
      type: 'smoothstep',
      pathOptions: { borderRadius: 12 },
      focusable: false,
      selectable: false,
      label: 'uses',
      labelStyle: { fill: 'rgba(190,190,190,0.75)', fontSize: 9 },
      labelBgStyle: { fill: 'transparent' },
      style: { stroke: 'rgba(190, 190, 190, 0.85)', strokeWidth: 1.5, strokeDasharray: '5 4' },
    });
    return true;
  };

  // Floating, connected to nothing — see the header.
  const floats = [];
  const float = (key, data) => floats.push({ key, data });

  for (const m of capabilities) {
    if (!m || !m.slug) continue;
    const key = capKey(m);
    const data = {
      name: capName(m),
      iconUrl: capIcons[key] || '',
      notReady: capReady[key] === false,
      onNodeClick: onOpenMember ? () => onOpenMember(m) : null,
    };
    if (m.depth) {
      // Held by a child agent → its card, if this canvas draws one. ONE LEVEL,
      // exactly like the member BOXES themselves, which draw this agent's
      // direct children and not its grandchildren: anything deeper is drawn on
      // the canvas of the agent that directly holds it, one click away through
      // the card it would have rolled up to.
      const box = ownerBoxId(m.owner, nodes, memberSlugByUuid);
      const ownerName = (m.owner && (m.owner.displayName || m.owner.workflowType)) || '';
      if (box) attach(box, key, { ...data, tooltip: `${data.name} is attached to ${ownerName}. Click to open it.` });
      continue;
    }
    const consumers = (Array.isArray(m.nodes) ? m.nodes : []).filter((nid) => byId.has(nid));
    if (consumers.length) {
      for (const nid of consumers) {
        attach(nid, key, { ...data, tooltip: `${data.name} is attached to this step. Click to open it.` });
      }
    } else {
      float(key, {
        ...data,
        tooltip: `${data.name} is attached to this agent — every step can use it. Click to open it.`,
      });
    }
  }

  for (const srv of customMcp) {
    if (!srv) continue;
    // The NAME is the label. Never the url and never the id — a raw
    // `717DCCEE21608860CEBB608374` or a host string on a customer's canvas is
    // the engineer-only label this whole pass exists to remove.
    const nm = srv.name || 'MCP server';
    float(`mcp:${srv.id || nm}`, {
      name: nm,
      iconUrl: '',
      notReady: false,
      tooltip: `${nm} is a custom MCP server added to this agent. Click to open its settings.`,
      onNodeClick: onOpenSettings || null,
    });
  }

  if (floats.length) {
    let minX = Infinity;
    let minY = Infinity;
    for (const n of nodes) {
      if (!n || !n.position) continue;
      if (n.position.x < minX) minX = n.position.x;
      if (n.position.y < minY) minY = n.position.y;
    }
    if (!Number.isFinite(minX)) { minX = 0; minY = 0; }
    floats.forEach((f, i) => {
      satNodes.push({
        id: `${MEMBER_NODE_PREFIX}${DISPATCH_MEMBER_PREFIX}agent__${f.key}`,
        type: 'memberNode',
        draggable: false,
        selectable: false,
        position: { x: minX + i * SAT_STEP, y: minY - AGENT_ROW_DY },
        data: { ...f.data, attached: false },
      });
    });
  }

  if (satNodes.length === 0) return { nodes, edges };
  return { nodes: [...nodes, ...satNodes], edges: [...edges, ...satEdges] };
}

export default withMemberSatellites;
